import { type FormEvent, useMemo, useState } from "react";
import { REBATE_LEVELS, REBATE_RULES } from "../config";

export function RebateProgram() {
  const [volume, setVolume] = useState("10000");
  const [rate, setRate] = useState("0.5");
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const estimatedRebate = useMemo(() => {
    const parsedVolume = Number(volume);
    const parsedRate = Number(rate);
    return Number.isFinite(parsedVolume) && Number.isFinite(parsedRate)
      ? Math.max(0, parsedVolume) * Math.max(0, parsedRate) / 100
      : 0;
  }, [rate, volume]);

  function submitApplication(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
  }

  return (
    <section className="section rebate-section" id="rebate">
      <div className="container">
        <div className="section-heading reveal">
          <p className="eyebrow">04 · 用户返佣与代理计划</p>
          <h2>从 L0 到 L3，升级条件与比例一次说清</h2>
          <p className="section-description">
            注册用户默认为 L0。达到有效邀请条件后自动升级 L1；L2 与 L3
            需手动申请，或由平台官方 BD 人员、已绑定上级调整返佣比例。
          </p>
        </div>

        <div className="rebate-levels">
          {REBATE_LEVELS.map((item) => (
            <article className={`rebate-level ${item.tone} reveal`} key={item.level}>
              <div className="level-top">
                <span>{item.level}</span>
                <strong>{item.rate}</strong>
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <div className="level-requirement">
                <small>升级方式</small>
                <strong>{item.requirement}</strong>
              </div>
            </article>
          ))}
        </div>

        <div className="rebate-details">
          <div className="qualification-card reveal">
            <div className="rebate-card-head">
              <span>有效用户判定</span>
              <small>L1 自动升级条件</small>
            </div>
            <div className="qualification-flow">
              <div>
                <span>01</span>
                <strong>成功邀请 3 人</strong>
                <small>必须是你的有效直属邀请用户</small>
              </div>
              <i>+</i>
              <div>
                <span>02</span>
                <strong>每人充值 &gt; 50 USDT</strong>
                <small>累计充值金额需高于 50 USDT</small>
              </div>
              <i>+</i>
              <div>
                <span>03</span>
                <strong>每人交易量 &gt; 500 USDT</strong>
                <small>累计交易量需高于 500 USDT</small>
              </div>
              <i>=</i>
              <div className="qualified-result">
                <span>L1</span>
                <strong>自动升级</strong>
                <small>交易量返佣 0.5%</small>
              </div>
            </div>
            <p className="rebate-note">{REBATE_RULES.note}</p>
          </div>

          <div className="rebate-calculator reveal">
            <div className="rebate-card-head">
              <span>返佣估算</span>
              <small>仅作公式演示</small>
            </div>
            <label>
              <span>统计交易量</span>
              <div><input type="number" min="0" value={volume} onChange={(event) => setVolume(event.target.value)} /><strong>USDT</strong></div>
            </label>
            <label>
              <span>返佣比例</span>
              <select value={rate} onChange={(event) => setRate(event.target.value)}>
                <option value="0">L0 · 0%</option>
                <option value="0.5">L1 · 0.5%</option>
                <option value="0.6">L2 · 0.6%</option>
                <option value="0.8">L2 · 0.8%</option>
                <option value="1">L2/L3 · 1%</option>
                <option value="1.2">L3 · 1.2%</option>
                <option value="1.5">L3 · 1.5%</option>
              </select>
            </label>
            <div className="rebate-result">
              <span>预估返佣</span>
              <strong>{estimatedRebate.toFixed(2)} <small>USDT</small></strong>
            </div>
            <p>计算公式：交易量 × 返佣比例。实际有效交易量、结算周期和可提现金额以平台统计为准。</p>
          </div>
        </div>

        <div className="agent-application reveal">
          <div>
            <p className="eyebrow">L2 / L3 APPLICATION</p>
            <h3>代理商申请方式</h3>
            <ol>
              <li><span>1</span><p><strong>准备账户信息</strong><small>账户 UID、当前等级、绑定上级与常用联系方式。</small></p></li>
              <li><span>2</span><p><strong>说明推广能力</strong><small>渠道类型、预计用户规模、目标市场和已有交易量。</small></p></li>
              <li><span>3</span><p><strong>提交平台审核</strong><small>可主动申请，或联系官方 BD / 已绑定上级协助调整。</small></p></li>
              <li><span>4</span><p><strong>确认最终比例</strong><small>平台返佣最低 0%，最高 1.5%；具体比例以账户显示为准。</small></p></li>
            </ol>
          </div>
          <div className="application-panel">
            {!applicationOpen ? (
              <>
                <span className="application-icon">↗</span>
                <h4>申请 L2 / L3 合作</h4>
                <p>先填写一份申请信息示例，确认需要准备的资料。本表不会发送给 Bitevent。</p>
                <button type="button" onClick={() => setApplicationOpen(true)}>查看申请表</button>
              </>
            ) : submitted ? (
              <div className="application-success">
                <span>✓</span>
                <h4>演示申请已记录</h4>
                <p>这只是前端演示，没有提交到任何平台。正式申请请通过 Bitevent 官方渠道、官方 BD 或你的已绑定上级办理。</p>
                <button type="button" onClick={() => { setSubmitted(false); setApplicationOpen(false); }}>完成</button>
              </div>
            ) : (
              <form onSubmit={submitApplication}>
                <div className="application-form-head">
                  <h4>代理申请信息示例</h4>
                  <button type="button" onClick={() => setApplicationOpen(false)}>×</button>
                </div>
                <label><span>账户 UID</span><input required placeholder="请输入 Bitevent UID" /></label>
                <label><span>联系方式</span><input required placeholder="Telegram / WhatsApp / 邮箱" /></label>
                <label>
                  <span>申请等级</span>
                  <select required defaultValue="L2"><option>L2</option><option>L3</option></select>
                </label>
                <label><span>推广渠道与计划</span><textarea required placeholder="简述渠道、目标市场、预计用户和交易规模" /></label>
                <button className="application-submit" type="submit">保存演示申请</button>
                <small>不会上传或发送数据，请勿填写敏感资料。</small>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
